import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';

const BusinessContext = createContext(null);
const STORAGE_KEY = 'flightdeck.businessId';

// Distinct accent per business so it's always obvious which one is active.
const PALETTE = ['#3b82f6', '#16a34a', '#9333ea', '#ea580c', '#0891b2', '#db2777'];

const FALLBACK_BUSINESSES = [
  { id: 1, name: 'Inflight Technology', short: 'ITL' },
  { id: 2, name: 'SIM360', short: 'S360' },
  { id: 3, name: 'Not My Airplane, LLC', short: 'B3' },
];

// Normalize ids to numbers (the API returns bigint as a string) and attach a stable color.
function decorate(rows) {
  return rows.map((r, i) => ({ ...r, id: Number(r.id), color: r.color || PALETTE[i % PALETTE.length] }));
}

export function BusinessProvider({ children }) {
  const [businesses, setBusinesses] = useState(() => decorate(FALLBACK_BUSINESSES));
  const [activeId, setActiveIdState] = useState(() => {
    const saved = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(saved) && saved > 0 ? saved : FALLBACK_BUSINESSES[0].id;
  });
  const setActiveId = (id) => setActiveIdState(Number(id));

  useEffect(() => {
    let cancelled = false;
    api('/businesses')
      .then((rows) => {
        if (cancelled || !Array.isArray(rows) || rows.length === 0) return;
        const list = decorate(rows);
        setBusinesses(list);
        // Keep the saved/current business if it still exists; only fall back otherwise.
        setActiveIdState((cur) => (list.some((b) => b.id === cur) ? cur : list[0].id));
      })
      .catch(() => { /* backend not up yet — keep fallback list */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, String(activeId)); }, [activeId]);

  const activeBusiness = useMemo(
    () => businesses.find((b) => b.id === activeId) || businesses[0],
    [businesses, activeId]
  );

  useEffect(() => {
    document.title = activeBusiness ? `Flightdeck · ${activeBusiness.name}` : 'Flightdeck';
  }, [activeBusiness]);

  const value = useMemo(
    () => ({ businesses, activeId, activeBusiness, setActiveId }),
    [businesses, activeId, activeBusiness]
  );

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export function useBusiness() {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusiness must be used within BusinessProvider');
  return ctx;
}
